import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { ETAPaymentMethod, isPhonePaymentMethod } from '~/api/ETA';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import Spinner from '~/components/Spinner/Spinner';
import { useGlobal } from '~/hooks/useGlobal';
import { CardDisclaimer } from '~/pages/Checkout/CardDisclaimer';
import { CashPayment } from '~/pages/Checkout/CashPayment';
import CARD_BRANDS from '~/pages/Checkout/cardBrands';
import MobileMoneyPayment from '~/pages/Checkout/MobileMoneyPayment';
import { useIsTablet } from '~/pages/Order/hooks';
import { checkoutStore, orderStore } from '~/stores';
import AdditionalPaymentMethod from '~/stores/CheckoutStore/AdditionalPaymentMethod';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import type PaymentMethod from '~/stores/CheckoutStore/PaymentMethod';

import {
  CurrentPaymentMethod,
  handlePaymentsChange,
  usePaymentMethodInit,
} from './paymentMethodsShared';

import './PaymentMethods.scss';

export const PaymentMethods = ({
  hideDisclaimer,
}: {
  hideDisclaimer?: boolean;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const isTablet = useIsTablet();

  usePaymentMethodInit();

  const iconSize = isMobile ? 20 : 24;

  const methodItemContent: Record<PaymentMethod, JSX.Element> = {
    [MobilePaymentMethod.ApplePay]: (
      <>
        <Icon
          {...(isMobile
            ? { type: 'apay2', size: 38 }
            : { type: 'apay', size: 24 })}
        />
        <div className="panel-section__content-payment-type-desctiption">
          <span>{t('applePay')}</span>
        </div>
        <input
          data-radio="apple-pay"
          type="radio"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === MobilePaymentMethod.ApplePay
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(MobilePaymentMethod.ApplePay)
          }
        />
      </>
    ),
    [MobilePaymentMethod.GooglePay]: (
      <>
        <Icon
          type="gpay"
          {...(isMobile
            ? { width: 46, height: 24 }
            : { width: 60, height: 32 })}
        />
        <div className="panel-section__content-payment-type-desctiption">
          <span>{t('googlePay')}</span>
        </div>
        <input
          data-radio="google-pay"
          type="radio"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === MobilePaymentMethod.GooglePay
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(MobilePaymentMethod.GooglePay)
          }
        />
      </>
    ),
    [ETAPaymentMethod.Card]: (
      <>
        <Icon type="add-card" size={iconSize} />
        <div className="panel-section__content-payment-type-desctiption">
          <span>
            {t(
              company.match({
                vilo: 'payOnline',
                default: 'newCardOnline',
              }),
            )}
          </span>
        </div>
        <input
          data-radio="card"
          type="radio"
          name="payment-type"
          checked={checkoutStore.activePaymentMethod === ETAPaymentMethod.Card}
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.Card)
          }
        />
      </>
    ),
    [ETAPaymentMethod.Cash]: (
      <>
        <Icon type="cash" size={iconSize} />
        <div className="panel-section__content-payment-type-desctiption">
          <span>{t('cash')}</span>
        </div>
        <input
          data-radio="cash"
          type="radio"
          name="payment-type"
          checked={checkoutStore.activePaymentMethod === ETAPaymentMethod.Cash}
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.Cash)
          }
        />
      </>
    ),
    [ETAPaymentMethod.PhoneAirtel]: (
      <>
        <Icon type="airtel" size={iconSize} />
        <div className="panel-section__content-payment-type-desctiption">
          <span>{t('paymentVia', { operator: 'Airtel' })}</span>
        </div>
        <input
          data-radio="mobile-money-airtel"
          type="radio"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === ETAPaymentMethod.PhoneAirtel
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.PhoneAirtel)
          }
        />
      </>
    ),
    [ETAPaymentMethod.PhoneMoov]: (
      <>
        <Icon type="moov" size={iconSize} />
        <div className="panel-section__content-payment-type-desctiption">
          <span>{t('paymentVia', { operator: 'Moov' })}</span>
        </div>
        <input
          data-radio="mobile-money-moov"
          type="radio"
          name="payment-type"
          checked={
            checkoutStore.activePaymentMethod === ETAPaymentMethod.PhoneMoov
          }
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.PhoneMoov)
          }
        />
      </>
    ),
    [ETAPaymentMethod.QR]: (
      <>
        <Icon type="qr-code" size={iconSize} />
        <div className="panel-section__content-payment-type-desctiption">
          <span>{t('mobileMoneyQR')}</span>
        </div>
        <input
          data-radio="mobile-money-qr"
          type="radio"
          name="payment-type"
          checked={checkoutStore.activePaymentMethod === ETAPaymentMethod.QR}
          onChange={() =>
            checkoutStore.setActivePaymentMethod(ETAPaymentMethod.QR)
          }
        />
      </>
    ),
    [AdditionalPaymentMethod.KNET]: (
      <div className="d-flex align-items-center">
        <img className="mr-6 flex-shrink-0" src={''} height="14" alt="" />
        {t('knet')}
      </div>
    ),
    [AdditionalPaymentMethod.VisaMastercard]: (
      <>
        {orderStore?.paymentCards?.map(
          ({ card: { brand: brandName, last4 }, id }) => {
            const cardBrand = brandName ? CARD_BRANDS[brandName] : '';
            const brandUi = cardBrand && (
              <>
                <img
                  src={cardBrand.icon}
                  alt={`payment system: ${brandName}`}
                />
                <div className="panel-section__content-payment-type-desctiption">
                  <span>{cardBrand.title}</span>
                  <span className="panel-section__content-payment-type-desctiption-card">
                    •• {last4}
                  </span>
                </div>
              </>
            );
            return (
              <Fragment key={id}>
                {brandUi}
                <input
                  data-radio={brandName}
                  type="radio"
                  name="payment-type"
                  checked={
                    checkoutStore.activePaymentMethod ===
                      AdditionalPaymentMethod.VisaMastercard &&
                    id === checkoutStore.paymentsMethodId
                  }
                  onChange={() => {
                    checkoutStore.setActivePaymentMethod(
                      AdditionalPaymentMethod.VisaMastercard,
                    );
                    handlePaymentsChange(id);
                  }}
                />
              </Fragment>
            );
          },
        )}
      </>
    ),
  };

  return (
    <div className="panel-section payment">
      {!isTablet && (
        <p className="panel-section__title side">
          {t('paymentMethod')}
          {!checkoutStore.activePaymentMethod && (
            <span className="c-red">*</span>
          )}
        </p>
      )}
      <div className="panel-section__content-payment">
        <div className="panel-section__content-payment-grid">
          {checkoutStore.availablePaymentMethods
            .filter((method) => method !== ETAPaymentMethod.QR || !isTablet)
            .map((method) => {
              return (
                <label
                  className={'panel-section__content-payment-type'}
                  key={method}
                >
                  {methodItemContent[method]}
                </label>
              );
            })}
        </div>
        {company.showCardDisclaimer &&
          checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
          !isPhonePaymentMethod(
            checkoutStore.activePaymentMethod as ETAPaymentMethod,
          ) &&
          checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR && (
            <CardDisclaimer />
          )}
        <div className="panel-section__content-payment-guarantee">
          <Icon
            type="shield"
            size={24}
            className="panel-section__content-payment-guarantee-icon"
          />
          <p className="panel-section__content-payment-guarantee-description">
            {t('securePayment')}
          </p>
        </div>
        {checkoutStore.activePaymentMethod === ETAPaymentMethod.Cash && (
          <CashPayment />
        )}
        {isPhonePaymentMethod(
          checkoutStore.activePaymentMethod as ETAPaymentMethod,
        ) && <MobileMoneyPayment />}

        <div className="panel-section__content-payment-add">
          <CurrentPaymentMethod />
          {!hideDisclaimer &&
            checkoutStore.activePaymentMethod !== ETAPaymentMethod.Cash &&
            !isPhonePaymentMethod(
              checkoutStore.activePaymentMethod as ETAPaymentMethod,
            ) &&
            checkoutStore.activePaymentMethod !== ETAPaymentMethod.QR &&
            !company.hideFreezeMoneyMessage && (
              <p className="panel-section__content-payment-add-text">
                {t('phrases:moneyFreeze')}
              </p>
            )}
        </div>
      </div>
    </div>
  );
};

export default observer(PaymentMethods);
